import { useState, useEffect } from 'react'
import { Check, X, Loader2 } from 'lucide-react'
import { useEquipmentUpdate } from '@/hooks/useEquipmentUpdate'

interface Equipment {
  id: string
  name: string
  part_number: string | null
  asset_number: string
  serial_number: string | null
  owner_id: string | null
  last_inspection_date: string | null
  next_inspection_date: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
  status: string
  inspection_status: string | null
  description: string | null
  model: string | null
  manufacture_year: number | null
  category_id: string | null
}

interface EditableEquipmentDetailsProps {
  equipment: Equipment
  onUpdate?: (updated: Equipment) => void
  source?: 'stmidb'
}

const EditableEquipmentDetails = ({ equipment, onUpdate, source }: EditableEquipmentDetailsProps) => {
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [fieldError, setFieldError] = useState<string | null>(null)

  const { updateEquipment, checkSerialNumberExists, updating } = useEquipmentUpdate()

  useEffect(() => {
    setEditingField(null)
    setEditValue('')
    setFieldError(null)
  }, [equipment])

  const startEdit = (field: string, value: string | null) => {
    setEditingField(field)
    setEditValue(value ?? '')
    setFieldError(null)
  }

  const cancelEdit = () => {
    setEditingField(null)
    setEditValue('')
    setFieldError(null)
  }

  const saveField = async () => {
    if (!editingField) return

    if (editingField === 'name' && !editValue.trim()) {
      setFieldError('Utstyrsnavn er påkrevd')
      return
    }

    if (
      source !== 'stmidb' &&
      editingField === 'serial_number' &&
      editValue &&
      editValue !== equipment.serial_number
    ) {
      const exists = await checkSerialNumberExists(editValue, equipment.id)
      if (exists) {
        setFieldError('Serienummeret er allerede i bruk')
        return
      }
    }

    const updates: Record<string, string | null> = {
      [editingField]: editValue || null,
    }

    const updated = await updateEquipment(equipment.id, updates, { source })
    if (updated) {
      cancelEdit()
      onUpdate?.(updated)
    } else {
      setFieldError('Kunne ikke lagre')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); saveField() }
    if (e.key === 'Escape') cancelEdit()
  }

  const inputCls =
    'w-full text-sm text-slate-800 bg-transparent focus:outline-none border-b border-slate-200 focus:border-[#1E3A5F] py-0.5 transition-colors'

  const ActionButtons = () => (
    <div className="flex gap-1.5 mt-2.5">
      <button
        onClick={saveField}
        disabled={updating}
        className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-white bg-[#1E3A5F] rounded hover:bg-[#152C4A] transition-colors disabled:opacity-50"
      >
        {updating ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
        Lagre
      </button>
      <button
        onClick={cancelEdit}
        className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded hover:bg-slate-50 transition-colors"
      >
        <X size={11} />
        Avbryt
      </button>
    </div>
  )

  const renderField = (fieldKey: string, label: string, value: string | null) => {
    const isActive = editingField === fieldKey

    return (
      <div
        key={fieldKey}
        className={`rounded-md px-3 py-2 border transition-colors ${
          isActive
            ? 'bg-white border-[#1E3A5F] shadow-sm'
            : 'bg-white border-slate-200 hover:border-[#1E3A5F] cursor-pointer group'
        }`}
        onClick={!isActive ? () => startEdit(fieldKey, value) : undefined}
      >
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 truncate leading-none mb-1">
          {label}
        </p>

        {isActive ? (
          <>
            <input
              autoFocus
              type="text"
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className={inputCls}
            />
            {fieldError && (
              <p className="text-xs text-red-600 mt-1">{fieldError}</p>
            )}
            <ActionButtons />
          </>
        ) : (
          <p className="text-sm font-semibold text-slate-800 group-hover:text-[#1E3A5F] truncate leading-snug transition-colors">
            {value || <span className="text-slate-300 font-normal">—</span>}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <div className="grid grid-cols-2 gap-2">
        {renderField('name', 'Utstyrsnavn', equipment.name)}
        {renderField('serial_number', 'Serienummer', equipment.serial_number)}
        {renderField('owner_id', 'Eiers ID', equipment.owner_id)}
        {renderField('part_number', 'Partnummer', equipment.part_number)}
      </div>
    </div>
  )
}

export default EditableEquipmentDetails
